import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ButtonSpinner from '../ButtonSpinner';
import { BTN_PRIMARY_SM } from '../buttonStyles';
import { busyStyle } from '../../utils/busyStyle';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { acceptLiveRoomCall, subscribeRoomCall, type LiveRoomCall } from '../../services/liveV2';
import { liveLessonsErrorMessage } from '../../services/liveLessons';
import { sideOfRoom } from '../../utils/liveRoom';

/**
 * Rule 9: a call in the pair's room outside every lesson, in the thread.
 * The server's line ("Ana wants to talk now. Accept to join. It isn't a
 * lesson and isn't recorded."), then Accept for the side that was asked,
 * which joins the room (acceptLiveRoomCall); Join for either side once it
 * connects; nothing once it is over.
 */
export function RoomCallBubble({ roomId, line }: { roomId: string; line: string }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showError } = useToast();
  const [call, setCall] = useState<LiveRoomCall | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => subscribeRoomCall(roomId, setCall), [roomId]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const me = currentUser ? sideOfRoom(roomId, currentUser.uid) : null;
  const live = !!call && new Date(call.expiresAt).getTime() > now;
  const askedMe = live && call?.status === 'waiting' && !!me && call.requestedBy !== me.side;
  const canJoin = live && call?.status === 'connected' && !!me;

  const accept = async () => {
    setBusy(true);
    try {
      await acceptLiveRoomCall({ roomId });
      navigate(`/room/${roomId}`);
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not join. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="my-3 flex justify-center" data-room-call>
      <div className="w-full max-w-[320px] rounded-[12px] border border-[#e6e6e6] bg-white px-4 py-3.5">
        <div className="text-[15px] leading-[20px] text-db-text-1">{line}</div>
        {askedMe ? (
          <button type="button" onClick={() => { void accept(); }} disabled={busy}
            aria-busy={busy} style={busyStyle(busy)} className={`${BTN_PRIMARY_SM} mt-3`}>
            {busy ? <ButtonSpinner size={16} /> : 'Accept'}
          </button>
        ) : canJoin ? (
          <button type="button" onClick={() => navigate(`/room/${roomId}`)} className={`${BTN_PRIMARY_SM} mt-3`}>
            Join
          </button>
        ) : call !== undefined && !live ? (
          <div className="mt-3 text-[13px] leading-[18px] text-db-text-2">No longer open.</div>
        ) : null}
      </div>
    </div>
  );
}
